import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Address, TextNodeType } from '@mwe/models';
import { DetailsLinkTypeEnum } from '@mwe/constants';
import { getAddressGroupFormatted, isArrayWithMinOneItem, isCancelledRelocationOrMoveOut } from '@mwe/utils';
import { TooltipCardComponent } from '../tooltip-card/tooltip-card.component';
import { NgbDropdown, NgbDropdownButtonItem, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { InfoboxComponent } from '../infobox/infobox.component';

export interface AddressDropdownItem {
  id: string;
  label: string;
  disabled?: boolean;
}

export type DropdownType = 'pen' | 'dropdown' | 'custom' | 'dots';

@Component({
  selector: 'mwe-address',
  templateUrl: './address.component.html',
  imports: [
    TooltipCardComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgClass,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    TranslatePipe,
    InfoboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressComponent {
  readonly addressIdx = input<number | string>();
  readonly dropdownType = input<DropdownType>();
  readonly address = input.required<Address>();
  readonly allowedUZorAZTooltipAddresses = input<string[]>(undefined);
  readonly dropdownEditKey = input<string>();
  readonly allDropdownItems = input<AddressDropdownItem[]>([]); // is split in featuredButtons and dropdownItems
  readonly featuredBtnIds = input<string[]>([]);
  readonly orderDateFormatted = input<string>();
  readonly recentOrderMsgKey = input<string>();
  readonly isArrowDownHidden = input<boolean>(false);
  readonly isAddressDetailsBodyVisible = input<boolean>(false);
  readonly productCategories = input<string[]>([]);
  readonly singleMarker = input(false);
  readonly textNodeType = input<TextNodeType>();
  readonly isEndedContract = input<boolean>();
  readonly hideTooltipBody = input<boolean>(false);

  readonly itemClick = output<string>();

  readonly addressHash = computed(() => this.address()?.hashCode);
  readonly dropdownEditId = computed<string>(() => `mwe-address-${this.addressIdx()}-dropdown`);
  readonly formattedAddress = computed<string>(() => getAddressGroupFormatted(this.address()));
  readonly featuredButtons = computed<AddressDropdownItem[]>(() => this.allDropdownItems()?.filter(i => this.hasId(i.id)));
  readonly shouldDisplayActionComponents = computed<boolean>(
    () => !this.isEndedContract() && (this.featuredButtons().length > 0 || !!this.dropdownType()),
  );
  readonly tooltipClass = computed<{ [value: string]: boolean }>(() => ({
    single: this.singleMarker(),
    'no-after': !!this.orderDateFormatted() || this.isArrowDownHidden(),
    'has-body':
      (!!this.address().addressGroup.detailsLinkType && this.isAddressDetailsBodyVisible()) || this.orderDateFormatted()?.length > 0,
  }));
  readonly detailsLinkItemKey = computed<string>(() => {
    if (
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.RELOCATION ||
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.RELOCATION_ERROR
    ) {
      return 'relocation.status';
    }

    if (
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.MOVEOUT ||
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.MOVEOUT_IGNORE_ERROR
    ) {
      return 'moveout.status';
    }

    return '';
  });
  readonly dropdownItems = computed<AddressDropdownItem[]>(() => {
    if (!isArrayWithMinOneItem(this.featuredBtnIds())) {
      return this.allDropdownItems() || [];
    }

    return this.allDropdownItems().filter(i => !this.hasId(i.id));
  });
  readonly hasTooltipBody = computed<boolean>(() => {
    return (
      ((this.address().addressGroup.detailsLinkType &&
        !isCancelledRelocationOrMoveOut(this.address().addressGroup.detailsLinkType) &&
        this.isAddressDetailsBodyVisible()) ||
        this.orderDateFormatted()?.length > 0) &&
      !this.isEndedContract() &&
      (this.allowedUZorAZTooltipAddresses()?.some(hash => this.addressHash() === hash) ?? true)
    );
  });

  executeItemAction(id: string): void {
    this.itemClick.emit(id);
  }

  public get detailsLinkTypeEnum(): typeof DetailsLinkTypeEnum {
    return DetailsLinkTypeEnum;
  }

  private hasId(id: string): boolean {
    return !!this.featuredBtnIds().find(fId => fId === id);
  }
}
